/**
 * Lightweight Sentry init for StudioScheduler.
 *
 * Activates ONLY when REACT_APP_SENTRY_DSN is present in the build env.
 * No DSN → fully no-op (zero overhead, zero network calls). This keeps
 * local development clean and lets prod opt-in just by setting the env var.
 *
 * Wired into:
 *   - index.js (init at app boot, must be the first import)
 *   - App.js (Sentry.ErrorBoundary wraps the router)
 *   - AuthContext.js (setUser on login, clearUser on logout)
 *   - ClientPages.js (breadcrumbs + captureException around booking & class-pass purchase)
 */
import * as Sentry from "@sentry/react";

const dsn = process.env.REACT_APP_SENTRY_DSN;

export function initSentry() {
  if (!dsn) return; // no-op when DSN missing
  Sentry.init({
    dsn,
    environment: process.env.NODE_ENV || "development",
    release: process.env.REACT_APP_VERSION || undefined,
    // Sample rates: keep generous in dev, dial down in prod.
    tracesSampleRate: process.env.NODE_ENV === "production" ? 0.1 : 1.0,
    // We don't want PII unless explicitly attached via setUser().
    sendDefaultPii: false,
    // Strip Authorization headers / cookies / tokens before transmission.
    // Defence in depth — keeps localStorage tokens & API keys out of Sentry
    // breadcrumbs and contexts even if some component accidentally logs them.
    beforeBreadcrumb(breadcrumb) {
      if (breadcrumb.data?.url && breadcrumb.data.url.includes("token=")) {
        breadcrumb.data.url = breadcrumb.data.url.replace(/token=[^&]+/, "token=<redacted>");
      }
      return breadcrumb;
    },
    beforeSend(event) {
      if (event.request?.headers) {
        delete event.request.headers.Authorization;
        delete event.request.headers.authorization;
        delete event.request.headers.cookie;
        delete event.request.headers.Cookie;
      }
      // Strip any token / api_key fields that may have ended up in extras.
      const SENSITIVE_KEYS = /^(.*)(token|password|secret|api[_-]?key|jwt|authorization)(.*)$/i;
      const scrub = (obj) => {
        if (!obj || typeof obj !== "object") return;
        for (const k of Object.keys(obj)) {
          if (SENSITIVE_KEYS.test(k)) obj[k] = "<redacted>";
          else if (typeof obj[k] === "object") scrub(obj[k]);
        }
      };
      scrub(event.contexts);
      scrub(event.extra);
      scrub(event.tags);
      return event;
    },
  });
}

/** Set authenticated user context. Called from AuthContext after login. */
export function setSentryUser(user) {
  if (!dsn || !user) return;
  Sentry.setUser({
    id: user.id,
    email: user.email,
    username: `${user.first_name || ""} ${user.last_name || ""}`.trim(),
  });
}

/** Clear user context. Called from AuthContext on logout. */
export function clearSentryUser() {
  if (!dsn) return;
  Sentry.setUser(null);
}

/**
 * Add a breadcrumb for a user action (e.g. "clicked Book Class").
 * Breadcrumbs are kept locally on the SDK and only attached if/when an
 * exception is captured later, so this is cheap to call frequently.
 */
export function recordBreadcrumb(category, message, data) {
  if (!dsn) return;
  Sentry.addBreadcrumb({
    category,
    message,
    level: "info",
    data: data || {},
  });
}

/**
 * Report an exception with structured context. Use inside catch blocks
 * for high-value flows like booking and payment.
 *
 * If the error is an ApiError (from src/api/client.ts) we automatically
 * attach status + body so silent destructure-style regressions surface
 * with full diagnostic information.
 */
export function reportError(err, ctx = {}) {
  if (!dsn) return;
  Sentry.withScope((scope) => {
    if (ctx.flow) scope.setTag("flow", ctx.flow);
    if (ctx.action) scope.setTag("action", ctx.action);
    if (err && err.name === "ApiError") {
      scope.setTag("api_status", String(err.status));
      scope.setContext("api_error", {
        status: err.status,
        body: err.body,
      });
    }
    if (ctx.extra) scope.setContext("extra", ctx.extra);
    Sentry.captureException(err);
  });
}

export { Sentry };
